import {
  BankTemplateDocumentation,
  CanonicalSuggestion,
  ValidationIssue,
  ValidationResult
} from './types'
import { getTemplateDocumentation } from './index'

type JsonSchema = Record<string, unknown>

interface WebMcpToolExecutionOptions {
  signal?: AbortSignal
}

interface WebMcpTool {
  name: string
  description: string
  inputSchema: JsonSchema
  execute: (
    input: Record<string, any>,
    options?: WebMcpToolExecutionOptions
  ) => Promise<unknown> | unknown
  annotations?: {
    readOnlyHint?: boolean
    consequentialHint?: boolean
    untrustedContentHint?: boolean
  }
}

interface WebMcpModelContext {
  registerTool: (
    tool: WebMcpTool,
    options?: { signal?: AbortSignal }
  ) => Promise<void>
}

export interface BankTemplateWebMcpState {
  templateId: string
  mode: 'new' | 'draft' | 'published'
  readonly: boolean
  name: string
  countryId: string
  originBankId: string
  destinationBankIds: string[]
  yaml: string
  validation: ValidationResult | null
  validating: boolean
  dirty: boolean
  destinationsChanged: boolean
  canPublish: boolean
  suggestions: CanonicalSuggestion[]
}

export interface BankTemplateWebMcpActions {
  getState: () => BankTemplateWebMcpState
  setYaml: (yaml: string) => Promise<void> | void
  validateYaml: (
    yaml?: string,
    signal?: AbortSignal
  ) => Promise<ValidationResult>
}

const getModelContext = (): WebMcpModelContext | null => {
  const documentWithWebMcp = document as Document & {
    modelContext?: WebMcpModelContext
  }
  return documentWithWebMcp.modelContext ?? null
}

const issueSummary = (issues: ValidationIssue[]) =>
  issues.slice(0, 12).map(issue => ({
    severity: issue.severity,
    message: issue.message,
    line: issue.line
  }))

const summarizeState = (state: BankTemplateWebMcpState) => ({
  templateId: state.templateId || null,
  mode: state.mode,
  readonly: state.readonly,
  name: state.name,
  countryId: state.countryId,
  originBankId: state.originBankId,
  destinationBankIds: state.destinationBankIds,
  yamlLength: state.yaml.length,
  validation: state.validation
    ? {
        valid: state.validation.valid,
        issues: issueSummary(state.validation.issues),
        issueCount: state.validation.issues.length
      }
    : null,
  validating: state.validating,
  dirty: state.dirty,
  destinationsChanged: state.destinationsChanged,
  canPublish: state.canPublish,
  suggestionCount: state.suggestions.length
})

const summarizeDocumentation = (documentation: BankTemplateDocumentation) => ({
  title: documentation.title,
  sections: documentation.sections.map(section => ({
    id: section.id,
    title: section.title,
    fileName: section.fileName,
    contentType: section.contentType,
    content: section.content
  })),
  examples: documentation.examples.map(example => ({
    id: example.id,
    title: example.title,
    fileName: example.fileName,
    contentType: example.contentType,
    content: example.content
  }))
})

const ensureEditable = (state: BankTemplateWebMcpState) => {
  if (state.readonly) {
    throw new Error(
      'La plantilla esta en modo lectura. El usuario debe crear un borrador antes de editar el YAML.'
    )
  }
}

const ensureYaml = (yaml: unknown): string => {
  if (typeof yaml !== 'string' || yaml.trim().length === 0) {
    throw new Error('El YAML es obligatorio y debe ser un texto no vacio.')
  }
  return yaml
}

export const registerBankTemplateWebMcpTools = (
  actions: BankTemplateWebMcpActions
): (() => void) => {
  const modelContext = getModelContext()
  if (!modelContext) return () => undefined

  const controller = new AbortController()
  const register = (tool: WebMcpTool) => {
    modelContext
      .registerTool(tool, { signal: controller.signal })
      .catch(error => {
        if (!controller.signal.aborted) {
          console.warn('[WebMCP] No se pudo registrar la herramienta', {
            tool: tool.name,
            error
          })
        }
      })
  }

  register({
    name: 'get_bank_template_context',
    description:
      'Lee el estado actual del estudio de plantilla bancaria, incluyendo modo, seleccion de bancos, validacion y cambios pendientes.',
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false
    },
    execute: async () => summarizeState(actions.getState()),
    annotations: {
      readOnlyHint: true,
      consequentialHint: false,
      untrustedContentHint: false
    }
  })

  register({
    name: 'get_bank_template_yaml',
    description:
      'Lee el YAML actual que el usuario esta editando en la plantilla bancaria.',
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false
    },
    execute: async () => {
      const state = actions.getState()
      return {
        readonly: state.readonly,
        yaml: state.yaml
      }
    },
    annotations: {
      readOnlyHint: true,
      consequentialHint: false,
      untrustedContentHint: true
    }
  })

  register({
    name: 'set_bank_template_yaml',
    description:
      'Reemplaza el YAML en el editor local. No guarda, publica ni ejecuta acciones irreversibles.',
    inputSchema: {
      type: 'object',
      properties: {
        yaml: {
          type: 'string',
          description: 'Contenido YAML completo que quedara en el editor.'
        }
      },
      required: ['yaml'],
      additionalProperties: false
    },
    execute: async input => {
      const state = actions.getState()
      ensureEditable(state)
      const yaml = ensureYaml(input.yaml)
      await actions.setYaml(yaml)
      return {
        ok: true,
        message:
          'YAML actualizado en el editor. El usuario debe guardar manualmente para persistirlo.',
        yamlLength: yaml.length
      }
    },
    annotations: {
      readOnlyHint: false,
      consequentialHint: false,
      untrustedContentHint: true
    }
  })

  register({
    name: 'replace_template_yaml',
    description:
      'Reemplaza un fragmento exacto del YAML local. No guarda ni publica la plantilla.',
    inputSchema: {
      type: 'object',
      properties: {
        target: {
          type: 'string',
          description: 'Texto exacto que se buscara dentro del YAML actual.'
        },
        replacement: {
          type: 'string',
          description: 'Texto que reemplazara el fragmento encontrado.'
        },
        occurrence: {
          type: 'number',
          description: 'Ocurrencia 1-indexed a reemplazar. Por defecto 1.'
        }
      },
      required: ['target', 'replacement'],
      additionalProperties: false
    },
    execute: async input => {
      const state = actions.getState()
      ensureEditable(state)
      const target = ensureYaml(input.target)
      const replacement =
        typeof input.replacement === 'string' ? input.replacement : ''
      const occurrence =
        typeof input.occurrence === 'number' && input.occurrence > 0
          ? Math.floor(input.occurrence)
          : 1

      let index = -1
      let from = 0
      for (let current = 0; current < occurrence; current += 1) {
        index = state.yaml.indexOf(target, from)
        if (index === -1) break
        from = index + target.length
      }
      if (index === -1) {
        throw new Error('No se encontro el fragmento indicado en el YAML.')
      }

      const yaml =
        state.yaml.slice(0, index) +
        replacement +
        state.yaml.slice(index + target.length)
      await actions.setYaml(yaml)
      return {
        ok: true,
        message:
          'Fragmento reemplazado en el editor. El usuario debe guardar manualmente.',
        yamlLength: yaml.length
      }
    },
    annotations: {
      readOnlyHint: false,
      consequentialHint: false,
      untrustedContentHint: true
    }
  })

  register({
    name: 'validate_bank_template_yaml',
    description:
      'Valida un YAML de plantilla bancaria con el backend. Si no se envia YAML, valida el contenido actual del editor.',
    inputSchema: {
      type: 'object',
      properties: {
        yaml: {
          type: 'string',
          description:
            'YAML opcional a validar. Si se omite, se usa el YAML actual del editor.'
        }
      },
      additionalProperties: false
    },
    execute: async (input, options) => {
      const result = await actions.validateYaml(input.yaml, options?.signal)
      return {
        valid: result.valid,
        issues: issueSummary(result.issues),
        issueCount: result.issues.length
      }
    },
    annotations: {
      readOnlyHint: true,
      consequentialHint: false,
      untrustedContentHint: true
    }
  })

  register({
    name: 'get_bank_template_documentation',
    description:
      'Lee desde el backend la documentacion oficial para construir YAML de plantillas bancarias, incluyendo secciones y ejemplos.',
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false
    },
    execute: async (_input, options) => {
      const documentation = await getTemplateDocumentation(options?.signal)
      return summarizeDocumentation(documentation)
    },
    annotations: {
      readOnlyHint: true,
      consequentialHint: false,
      untrustedContentHint: false
    }
  })

  register({
    name: 'list_template_standards',
    description:
      'Resume los estandares para construir YAML de plantillas bancarias en este editor.',
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false
    },
    execute: async () => ({
      root: 'El documento debe iniciar en Template:',
      output:
        'Template.output define format, extension, encoding, lineEnding, delimiter y fileName.',
      records:
        'Template.records contiene registros header, body y footer. Cada registro tiene columns; body puede incluir sortBy.',
      columns:
        'Cada columna usa key, value y, para archivos posicionales, length. Puede usar align, fill { char, side }, truncate, format y required.',
      expressions:
        'Las expresiones se escriben como {{ruta.canonica}} o funciones inline como {{digitsOnly(...)}}.',
      validation:
        'La validacion oficial siempre la ejecuta el backend. Guardar y publicar son acciones manuales del usuario.',
      commonFunctions: [
        'digitsOnly',
        'upper',
        'lower',
        'trim',
        'removeAccents',
        'fixedAmount'
      ]
    }),
    annotations: {
      readOnlyHint: true,
      consequentialHint: false,
      untrustedContentHint: false
    }
  })

  return () => controller.abort()
}
